import React from "react";
import { useQuery } from "@tanstack/react-query";

import Card from "../ui/Card";
import { PageFirstLoadSpinner } from "../ui/PageFirstLoadSpinner.jsx";
import { leadsService } from "../../services/leads";
import AuditDashboardKpis from "./dashboard/AuditDashboardKpis";
import AuditHistogramChart from "./dashboard/AuditHistogramChart";
import AuditSegmentExplorer from "./dashboard/AuditSegmentExplorer";
import AuditSalesProspects from "./dashboard/AuditSalesProspects";
import AuditEmailTypeDonut from "./dashboard/AuditEmailTypeDonut";
import AuditBrandFitRings from "./dashboard/AuditBrandFitRings";

/**
 * @param {string} mailboxHash
 * @param {{ data: object|undefined, isLoading: boolean, isError: boolean }} [mailboxAudit] prefetched API payload from parent
 */
export default function AuditDashboard({ mailboxHash = "", mailboxAudit = null }) {
  const mailboxFallbackQuery = useQuery({
    queryKey: ["mailbox-audit-results", mailboxHash, "all"],
    queryFn: () => leadsService.getMailboxAuditResults(mailboxHash, "all"),
    enabled: mailboxAudit == null && Boolean(mailboxHash),
  });

  const useMailboxFromParent = mailboxAudit != null;
  const data = useMailboxFromParent ? mailboxAudit.data : mailboxFallbackQuery.data;
  const err = useMailboxFromParent ? mailboxAudit.isError : mailboxFallbackQuery.isError;
  const loading = useMailboxFromParent
    ? mailboxAudit.isLoading
    : mailboxFallbackQuery.isLoading && !mailboxFallbackQuery.data;

  if (!mailboxHash) {
    return null;
  }

  if (loading && !data) {
    return <PageFirstLoadSpinner />;
  }

  if (err) {
    return (
      <Card className="p-8 text-center border border-theme-border">
        <p className="text-theme-muted text-sm">Could not load audit insights.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <AuditDashboardKpis kpis={data?.kpis} />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-stretch">
        <AuditEmailTypeDonut rows={data?.by_email_type} />
        <AuditBrandFitRings radial={data?.brand_radial} avgFitScore={data?.kpis?.avg_lead_fit_score} />
        <AuditHistogramChart histogram={data?.score_histogram} />
      </div>

      <AuditSegmentExplorer auditData={data} />

      <AuditSalesProspects prospects={data?.sales_prospects_preview} taskStatus={data?.task_status} />
    </div>
  );
}
